import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import { BsQuestionSquare } from 'react-icons/bs'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, Chip, IconButton, Tooltip } from '@mui/material'

import Image from '@components/Image'
import useIsMounted from '@hooks/useIsMounted'
import UserService from '@services/users/user.service'
import { TableView } from '@components/customized/datatable'
import { colorSelection } from '@utils/colorSelection'
import { AuthContext } from '@contexts/Auth'
import { hasPermission } from '@utils/permission_helper'
import { PermissionCodeEnum } from '@models/permission'

interface ProjectAccess {
	id?: string
	active?: boolean
	code?: string
	dashboard_url?: string
	display_name?: string
	haveAccess?: boolean
	icon_url?: string
	item_id?: string
	item_type?: string
}

interface UsersGroup {
	id?: string
	active?: boolean
	code?: string
	display_name?: string
	project_access?: ProjectAccess[]
	org_ids?: string[]
	project_subs?: any[]
	is_admin?: boolean
}

export default function UsersGroupsTable({ setProject, setUserGroups }: any) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { state: authState } = useContext(AuthContext)
	const isMounted = useIsMounted()
	const refMounted = useRef(true)
	const { project_id } = useParams<string>()
	const defaultFilter = {
		project_ids: { operator: '=', value: [project_id] },
		active: { operator: '=', value: [true] },
	}

	const [filterData, setFilterData] = useState<any>(null)
	const [filterOptions, setFilterOptions] = useState<any>({})

	useEffect(() => {
		if (filterData) {
			delete filterData.pagination
			delete filterData.sort
			delete filterData.active
			UserService.GetRolesOptions(filterData).then((rep) => {
				setFilterOptions(rep)
			})
		}
	}, [filterData])

	return (
		<>
			<TableView
				columns={[
					{
						key: 'display_name',
						desc: t('User Role'),
						renderCell: (params: UsersGroup) => {
							return (
								<Fragment>
									<Tooltip title={params?.display_name}>
										<Box sx={{ 'white-space': 'break-spaces' }}>{params?.display_name}</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					// hardcoded for hkland project
					// {
					// 	key: 'project_access',
					// 	desc: t('Project Access'),
					// 	renderCell: (params: UsersGroup) => {
					// 		const project_access = params?.project_access?.map(
					// 			(x: ProjectAccess, index: number) => {
					// 				return (
					// 					<Tooltip title={x?.display_name ?? x?.code ?? ''} key={`project_${x.id}`}>
					// 						{x?.icon_url ? (
					// 							<Image
					// 								src={x?.icon_url}
					// 								width={30}
					// 								height={30}
					// 								themeColor={x.haveAccess ? colorSelection(x?.code ?? 'error') : 'info'}
					// 								sx={{ margin: '0px 10px 0px 10px' }}
					// 							/>
					// 						) : (
					// 							<span>
					// 								<BsQuestionSquare
					// 									style={{
					// 										fontSize: '30px',
					// 										color: x.haveAccess ? 'green' : '#707070',
					// 										margin: '0px 5px 0px 5px',
					// 									}}
					// 								/>
					// 							</span>
					// 						)}
					// 					</Tooltip>
					// 				)
					// 			},
					// 		)
					// 		return <Box sx={{ 'white-space': 'break-spaces' }}>{project_access}</Box>
					// 	},
					// 	disableSort: true,
					// },
					{
						key: 'id',
						desc: t('Function Buttons'),
						renderCell: (params: UsersGroup) => {
							return (
								<>
									{hasPermission(authState?.userInfo, [PermissionCodeEnum.RoleUpdate]) && (
										<IconButton
											onClick={(e) => {
												if (project_id && params?.id) {
													e.stopPropagation()
													navigate(
														`/configurations/users_config/projects/user_groups/edit_user_group/${project_id}/${params?.id}`,
													)
												}
											}}
										>
											<Tooltip title={'Edit User Role'}>
												<EditIcon>{t('Edit')}</EditIcon>
											</Tooltip>
										</IconButton>
									)}
									{params.is_admin && <Chip label="Admin" color="primary" />}
								</>
							)
						},
						disableSort: true,
					},
				]}
				reloadCallback={async (req: any) => {
					setFilterData(req)
					if (refMounted.current && isMounted()) {
						const resp = await UserService.GetMyProfileRoles(req)
						const userGroupsData = resp?.data?.map((element) => {
							return {
								...element,
								project_subs: element.project_subs,
							}
						})
						const totalCount = resp?.total_count
						const projectDetails = await UserService.GetProject(project_id ?? '')
						setProject(projectDetails)

						console.log('projectDetails', projectDetails)
						console.log('userGroupsData', userGroupsData)

						const result = userGroupsData.map((userGroup) => {
							return {
								...userGroup,
								project_access: projectDetails?.subscriptions?.map((x: any) => {
									return {
										...x,
										haveAccess: userGroup?.project_subs
											?.map((item) => item.item_id)
											?.includes(x?.item_id ?? ''),
										is_admin: userGroup.is_admin,
										org_ids: userGroup.org_ids,
									}
								}),
							}
						})
						console.log('result', result)
						setUserGroups(result)
						return { data: result, total_count: totalCount }
					}
				}}
				toolbar={true}
				mountedRef={refMounted}
				extraButton={
					hasPermission(authState?.userInfo, [PermissionCodeEnum.RoleCreate]) ? (
						<Button
							variant="contained"
							onClick={() =>
								navigate(
									`/configurations/users_config/projects/user_groups/create_user_group/${project_id}`,
								)
							}
							color="defaultButton"
						>
							{t('Create Role')}
						</Button>
					) : undefined
				}
				haveGlobalSearch={true}
				criterion={[
					{
						key: 'display_name',
						name: t('User Role'),
						type: 'StringFilter',
					},
					{
						key: 'active',
						name: t('Active'),
						type: 'ActiveFilter',
					},
				]}
				defaultFilterCriteria={defaultFilter}
				tableHeight="580px"
				defaultSorter={{ by: 'display_name', order_by: 'DESC' }}
			/>
		</>
	)
}
